import React, { ReactNode } from 'react';

import Button from '@/common/components/ui/Button';
import { QuizQuestionData } from 'quiz-types';
import { CheckmarkCircleIcon } from '@/assets/ui';
import { getInputClassName } from '@/common/components/input/StandardInputStyles';

interface Props {
  questionData: QuizQuestionData;
  onAnswerClick?: (questionIndex: number, optionIndex: number) => void;
  onAnswerInput?: (questionIndex: number, value: string) => void;
  currentAnswer?: number | string;
}

function QuizQuestion({ questionData, onAnswerClick, onAnswerInput, currentAnswer }: Props) {
  const renderMC = (): ReactNode => {
    if (!questionData.options) {
      return <span>Content not found. Please reload.</span>;
    }
    return questionData.options.map((option, answerIndex) => (
      <div className='w-full my-2' key={`question-${option.index}`}>
        <Button
          className={`flex flex-row gap-2 items-center justify-start w-full h-fit rounded-[8px] p-2 border-2
            ${
              currentAnswer != null && currentAnswer !== ''
                ? `${
                    // When something is selected, keep selected highlighted and others neutral
                    // We do not reveal correctness here; use primary highlight for selection
                    // eslint-disable-next-line no-nested-ternary
                    currentAnswer == option.index
                      ? 'bg-primary_highlight border-primary_highlight'
                      : 'bg-surface_gray_25_hover dark:border-border_gray_300'
                  }`
                : 'bg-surface_gray_25_hover dark:border-border_gray_300 hover:bg-primary_highlight hover:border-primary_highlight hover:text-primary cursor-pointer'
            }`}
          outline={true}
          onClick={() => onAnswerClick?.(questionData.index, option.index)}
        >
          {/* ANSWER LETTER CHIP */}
          <span
            className={`font-semibold text-base leading-6 tracking-[0%] text-center rounded-[4px] w-6 h-6 flex items-center justify-center
              ${
                currentAnswer == option.index
                  ? 'bg-white text-primary border-2 border-primary'
                  : 'bg-primary text-text_contrast'
              }
            `}
          >
            {String.fromCharCode(answerIndex + 97).toUpperCase()}
          </span>

          {/* ANSWER TEXT */}
          <span
            className={`flex-grow text-start font-semibold text-base leading-6 tracking-[0%]
              ${
                currentAnswer != null && currentAnswer !== ''
                  ? `${currentAnswer == option.index ? 'text-primary' : 'text-text_gray_800'}`
                  : 'text-inherit'
              }`}
          >
            {option.value}
          </span>

          {/* Selected indicator (kept for functionality parity) */}
          <CheckmarkCircleIcon
            className={`w-6 bg-success rounded-full p-1 ml-2 ${currentAnswer == option.index ? '' : 'hidden'}`}
          />
        </Button>
      </div>
    ));
  };

  const renderSA = (): ReactNode => {
    if (!questionData) {
      return <span>Content not found. Please reload.</span>;
    }
    return (
      <div className='mt-8'>
        <input
          placeholder='Answer question here...'
          className={getInputClassName('text')}
          type='text'
          maxLength={250}
          value={currentAnswer ?? ''}
          onChange={(e) => onAnswerInput?.(questionData.index, e.target.value)}
        />
      </div>
    );
  };

  const renderFR = (): ReactNode => {
    if (!questionData) {
      return <span>Content not found. Please reload.</span>;
    }
    return (
      <div className='flex flex-grow mt-8'>
        <textarea
          placeholder='Answer question here...'
          className={`${getInputClassName('textarea')} resize-none min-h-[8rem] mb-8`}
          maxLength={1999}
          value={currentAnswer}
          onChange={(e) => onAnswerInput?.(questionData.index, e.target.value)}
        />
      </div>
    );
  };

  const renderQuestionData = (): ReactNode => {
    if (!questionData) {
      return <span>There was an error loading your quiz data. Please refresh.</span>;
    }

    switch (questionData.type) {
      case 'Multiple Choice':
        if (!questionData.options) {
          return <span>There was an error loading your quiz data. Please refresh.</span>;
        }
        return renderMC();
      case 'Short Answer':
        return renderSA();
      case 'Paragraph':
        return renderFR();
      default:
        return <span>Content not found. Please reload.</span>;
    }
  };

  return (
    <div id={`question-${questionData.index}`} className='flex flex-col flex-grow'>
      <div className='break-words text-center font-semibold text-base leading-6 tracking-[0%]'>
        {questionData.question}
      </div>
      {renderQuestionData()}
    </div>
  );
}

export default QuizQuestion;
